import type { GeocodeResult } from '@/types/kakao';
import { useEffect, useRef } from 'react';

interface MapPosition {
  latitude: number;
  longitude: number;
  name: string;
  address: string;
  type?: 'well' | 'waterSupply' | 'management';
}

interface MapOptions {
  positions: MapPosition[];
  level?: number;
  center?: { latitude: number; longitude: number };
  onClick?: (latitude: number, longitude: number) => void;
}

interface KakaoLatLng {
  getLat: () => number;
  getLng: () => number;
}

interface KakaoMap {
  setBounds: (bounds: KakaoLatLngBounds) => void;
  setMapTypeId: (mapTypeId: string) => void;
  panTo: (latlng: KakaoLatLng) => void;
  setCenter: (latlng: KakaoLatLng) => void;
  setLevel: (level: number) => void;
}

interface KakaoMarker {
  setMap: (map: KakaoMap | null) => void;
}

interface KakaoInfoWindow {
  open: (map: KakaoMap, marker: KakaoMarker) => void;
  close: () => void;
}

interface KakaoLatLngBounds {
  extend: (latlng: KakaoLatLng) => void;
}

declare global {
  interface Window {
    kakao: {
      maps: {
        LatLng: new (lat: number, lng: number) => KakaoLatLng;
        LatLngBounds: new () => KakaoLatLngBounds;
        Map: new (
          container: HTMLElement,
          options: { center: KakaoLatLng; level: number }
        ) => KakaoMap;
        Marker: new (options: { position: KakaoLatLng }) => KakaoMarker;
        InfoWindow: new (options: { content: string }) => KakaoInfoWindow;
        event: {
          addListener: (
            target: KakaoMarker | KakaoMap,
            type: string,
            handler: (mouseEvent?: { latLng: KakaoLatLng }) => void
          ) => void;
        };
        services: {
          Geocoder: new () => {
            addressSearch: (
              address: string,
              callback: (result: GeocodeResult[], status: string) => void
            ) => void;
          };
          Status: {
            OK: string;
          };
        };
        MapTypeId: {
          ROADMAP: string;
          SKYVIEW: string;
          HYBRID: string;
        };
        load: (callback: () => void) => void;
      };
    };
  }
}

const getMarkerColor = (type: MapPosition['type']) => {
  switch (type) {
    case 'well':
      return '#3B82F6'; // 파란색 - 관정
    case 'waterSupply':
      return '#10B981'; // 녹색 - 급수지
    case 'management':
      return '#F59E0B'; // 주황색 - 관리사
    default:
      return '#6B7280';
  }
};

const getTypeLabel = (type: MapPosition['type']) => {
  switch (type) {
    case 'well':
      return '취수원';
    case 'waterSupply':
      return '배수지';
    case 'management':
      return '관리사';
    default:
      return '';
  }
};

// 유효한 좌표인지 확인하는 헬퍼 함수
const isValidPosition = (
  pos: MapPosition | { latitude: number; longitude: number }
) => {
  return (
    pos &&
    typeof pos.latitude === 'number' &&
    typeof pos.longitude === 'number' &&
    pos.latitude !== 0 &&
    pos.longitude !== 0 &&
    !isNaN(pos.latitude) &&
    !isNaN(pos.longitude)
  );
};

export function useKakaoMap(isOpen: boolean, options?: MapOptions) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<KakaoMap | null>(null);
  const markersRef = useRef<KakaoMarker[]>([]);

  // 지도 초기화
  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;

    const waitForKakaoAPI = () => {
      if (isCancelled) return;

      if (!window.kakao?.maps) {
        console.warn('⏳ 카카오 맵 API 로딩 중...');
        setTimeout(waitForKakaoAPI, 100);
        return;
      }

      const initMap = (retryCount = 0) => {
        if (isCancelled) return;

        const container = mapRef.current;

        if (!container) {
          if (retryCount < 100) {
            // 10초까지 대기
            setTimeout(() => initMap(retryCount + 1), 200);
          } else {
            console.error('❌ DOM 컨테이너를 찾을 수 없습니다.');
          }
          return;
        }

        // 컨테이너가 보이는지 확인
        const rect = container.getBoundingClientRect();
        const isVisible = rect.width > 0 && rect.height > 0;

        if (!isVisible) {
          if (retryCount < 100) {
            // 10초까지 대기
            console.log(
              `⏳ 컨테이너 크기 대기 중... (시도: ${retryCount + 1}/100)`
            );
            setTimeout(() => initMap(retryCount + 1), 200);
          } else {
            console.error(
              '❌ 컨테이너 크기가 0입니다. CSS 스타일을 확인하세요.'
            );
          }
          return;
        }

        try {
          // 기본 중심점 (서울시청)
          const defaultCenter = {
            latitude: 37.5642135,
            longitude: 127.0016985,
          };

          const mapOptions = {
            center: new window.kakao.maps.LatLng(
              defaultCenter.latitude,
              defaultCenter.longitude
            ),
            level: 3,
          };

          const map = new window.kakao.maps.Map(container, mapOptions);
          map.setMapTypeId(window.kakao.maps.MapTypeId.HYBRID);

          mapInstanceRef.current = map;
          console.log('✅ 카카오 맵 인스턴스 생성 완료');

          // 지도 클릭 이벤트 리스너 등록
          if (options?.onClick) {
            window.kakao.maps.event.addListener(map, 'click', (mouseEvent) => {
              if (!mouseEvent?.latLng) return;
              const latlng = mouseEvent.latLng;
              const lat = latlng.getLat();
              const lng = latlng.getLng();
              options.onClick?.(lat, lng);
            });
          }
        } catch (error) {
          console.error('❌ 카카오 맵 생성 중 오류:', error);
        }
      };

      // 100ms 후 초기화 시작 (DOM이 완전히 렌더링되도록)
      setTimeout(() => initMap(), 100);
    };

    // 카카오 API 로딩 대기
    waitForKakaoAPI();

    return () => {
      // cleanup 플래그 설정
      isCancelled = true;

      // 마커들 정리
      markersRef.current.forEach((marker) => {
        try {
          marker.setMap(null);
        } catch {
          // 이미 제거된 마커 무시
        }
      });
      markersRef.current = [];

      // 지도 인스턴스 정리
      mapInstanceRef.current = null;

      console.log('🧹 카카오 맵 cleanup 완료');
    };
  }, [isOpen]);

  // 마커 및 지도 위치 업데이트
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!options?.positions.length) {
      return;
    }

    // 유효한 위치만 필터링
    const validPositions = options.positions.filter(isValidPosition);

    if (validPositions.length === 0) {
      console.warn('⚠️ 유효한 위치 정보가 없습니다.');
      return;
    }

    // 지도 인스턴스가 생성될 때까지 최대 5초 기다리기
    const waitForMapInstance = (attempts = 0) => {
      if (mapInstanceRef.current) {
        updateMarkers();
        return;
      }

      if (attempts < 50) {
        // 50번 시도 (5초)
        setTimeout(() => waitForMapInstance(attempts + 1), 100);
      } else {
        console.error('❌ 지도 인스턴스 생성 대기 시간 초과 (5초)');
        console.error('💡 해결 방법:');
        console.error('  1. 카카오 맵 API 키를 확인하세요 (index.html)');
        console.error('  2. 네트워크 연결을 확인하세요');
        console.error(
          '  3. 브라우저 개발자 도구 > 네트워크 탭에서 카카오 맵 API 로딩 상태를 확인하세요'
        );
        console.error(
          '  4. 컨테이너 CSS 스타일 (display, width, height)을 확인하세요'
        );
      }
    };

    const updateMarkers = () => {
      if (!mapInstanceRef.current) {
        console.error('❌ updateMarkers: 지도 인스턴스가 없습니다');
        return;
      }

      const map = mapInstanceRef.current;

      // 기존 마커들 제거
      markersRef.current.forEach((marker) => {
        marker.setMap(null);
      });
      markersRef.current = [];

      // 새로운 마커들 추가 (유효한 위치만)
      const markers: KakaoMarker[] = [];
      const infoWindows: KakaoInfoWindow[] = [];

      validPositions.forEach((position) => {
        try {
          const markerPosition = new window.kakao.maps.LatLng(
            position.latitude,
            position.longitude
          );

          const marker = new window.kakao.maps.Marker({
            position: markerPosition,
          });

          marker.setMap(map);
          markers.push(marker);

          const typeLabel = getTypeLabel(position.type);
          const color = getMarkerColor(position.type);

          const infoWindow = new window.kakao.maps.InfoWindow({
            content: `
              <div style="padding: 12px; min-width: 200px; font-family: -apple-system, BlinkMacSystemFont, sans-serif;">
                <div style="display: flex; align-items: center; margin-bottom: 8px;">
                  <div style="width: 12px; height: 12px; background-color: ${color}; border-radius: 50%; margin-right: 8px;"></div>
                  <span style="font-weight: bold; color: #374151;">${typeLabel}</span>
                </div>
                <div style="font-weight: 600; color: #111827; margin-bottom: 4px;">${position.name}</div>
                <div style="font-size: 13px; color: #6B7280; line-height: 1.4;">${position.address}</div>
              </div>
            `,
          });

          infoWindows.push(infoWindow);

          const handleMarkerClick = () => {
            // 다른 정보창들 닫기
            infoWindows.forEach((iw) => {
              try {
                iw.close();
              } catch {
                // close 메서드가 없는 경우 무시
              }
            });
            // 클릭한 마커의 정보창 열기
            infoWindow.open(map, marker);
          };

          window.kakao.maps.event.addListener(
            marker,
            'click',
            handleMarkerClick
          );
        } catch (error) {
          console.error('❌ 마커 생성 실패:', position, error);
        }
      });

      markersRef.current = markers;

      // 지도 중심점 및 레벨 조정
      // center가 없으면 첫 번째 유효한 위치 사용
      const centerPos = options.center
        ? isValidPosition(options.center as MapPosition)
          ? options.center
          : validPositions[0]
        : validPositions[0];

      if (!centerPos) {
        console.error('❌ 유효한 중심 좌표를 찾을 수 없습니다.');
        return;
      }

      const center = new window.kakao.maps.LatLng(
        centerPos.latitude,
        centerPos.longitude
      );

      // 지도 중심 이동 (애니메이션 효과와 함께)
      try {
        map.panTo(center);
      } catch {
        // panTo가 지원되지 않는 경우 setCenter 사용
        map.setCenter(center);
      }

      // 레벨 설정
      if (options.level) {
        try {
          map.setLevel(options.level);
        } catch (error) {
          console.warn('지도 레벨 설정 실패:', error);
        }
      }

      // 모든 마커가 보이도록 지도 범위 조정 (마커가 2개 이상일 때)
      if (validPositions.length > 1) {
        try {
          const bounds = new window.kakao.maps.LatLngBounds();
          validPositions.forEach((position) => {
            bounds.extend(
              new window.kakao.maps.LatLng(
                position.latitude,
                position.longitude
              )
            );
          });
          map.setBounds(bounds);
        } catch (error) {
          console.warn('지도 범위 조정 실패:', error);
        }
      }
    };

    waitForMapInstance();
  }, [isOpen, options]);

  return { mapRef };
}
